import React, { useState, useMemo, useEffect, useCallback } from "react";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { Tabs, Tab, Form, Dropdown, Col, Button, Row } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import RadioButton from "components/radio-button";
import ToggleSwitch from "components/toggle-switch";
import ErrorMessage from "components/error-message";
import SuccessMessage from "components/success-message";
import CustomSelect from "components/custom-select/CustomSelect";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import { LANGUAGE_SPOKEN_OPTIONS } from "components/provider-intake/utility/constants";
import postOrchidPro from "services/postOrchidPro";
import {
  NOTE_FORMATTING,
  NOTE_TONE,
  NOTE_VERBOSITY,
  SERVICE_PROVIDER_DEFAULT,
  SERVICE_RECEIVER_DEFAULT,
} from "./constants";
import { PatientNote } from "services/queryPatientNotes";
import getClasses from "utils/getClasses";
import styles from "./AISetting.module.scss";
import IconButton from "components/icon-button";
import { getServiceProviderOptions } from "./getServiceProviderOptions";
import ConfirmModal from "components/confirm-modal";
import PreviousNotesList from "./PreviousNoteList";
import AutoDeleteTranscript from "../transcription/AutoDeleteTranscript";
import { useQuery } from "@tanstack/react-query";
import userSettingsQueryOptions from "queries/userSettingsQueryOptions";
import useSaveUserSettingsMutation from "mutations/useSaveUserSettingsMutation";
import { useUser } from "contexts/UserContext";
import AutoDeleteRecordingsOnNoteSigning from "../transcription/AutoDeleteRecordingsOnNoteSigning";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-ai";

const AI_SETTING_OPTIONS = [
  { value: "recording-preferences", label: "Recording Preferences" },
  { value: "ai-notes", label: "AI generated Notes" },
  { value: "treatment-plan", label: "Treatment Plan" },
];

const AISetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const navigate = useNavigate();

  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const { orchidPro, setOrchidPro } = useOrchidPro();
  const [tab, setTab] = useState(activeTab || "ai-notes");
  const [changed, setChanged] = useState(false);
  const [success, setSuccess] = useState<string>();
  const [error, setError] = useState<any>();
  const [suggestCPT, setSuggestCPT] = useState(true);
  const [suggestICD, setSuggestICD] = useState(true);
  const [draftTreatmentPlan, setDraftTreatmentPlan] = useState(true);

  const [customizeNoteStyle, setCustomizeNoteStyle] = useState(true);

  const [patientNote, setPatientNote] = useState<PatientNote>();

  const [noteFormatting, setNoteFormatting] = useState("paragraph");
  const [noteTone, setNoteTone] = useState("objective");
  const [noteVerbosity, setNoteVerbosity] = useState("concise");

  const [serviceProvider, setServiceProvider] = useState("Therapist");
  const [serviceReceiver, setServiceReceiver] = useState("Client");

  const [serviceProviderOptions, setServiceProviderOptions] = useState(
    SERVICE_PROVIDER_DEFAULT,
  );
  const [serviceReceiverOptions, setServiceReceiverOptions] = useState(
    SERVICE_RECEIVER_DEFAULT,
  );

  const [enableAILearning, setEnableAILearning] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [enableAIVideo, setEnableAIVideo] = useState(false);
  const [enableRecorder, setEnableRecorder] = useState(false);

  const [enableSmartGoals, setEnableSmartGoals] = useState(false);

  const [language, setLanguage] = useState("English");
  const { user } = useUser();
  const userSettingsQuery = useQuery({
    ...userSettingsQueryOptions({ userId: user?.sub || "" }),
    enabled: !!user?.sub,
  });
  const updateUserSettingsMutation = useSaveUserSettingsMutation({
    userId: user?.sub || "",
  });
  const [autoDeleteTranscript, setAutoDeleteTranscript] = useState(false);
  const [autoDeleteAfterDays, setAutoDeleteAfterDays] = useState<number>(
    userSettingsQuery.data?.audioRecorder?.autoDeletionFrequency ?? 7,
  );
  const [
    autoDeleteRecordingsOnNoteSignings,
    setAutoDeleteRecordingsOnNoteSignings,
  ] = useState(false);

  useEffect(() => {
    const deleteFreq =
      userSettingsQuery.data?.audioRecorder?.autoDeletionFrequency;
    const isDeleteEnabled =
      userSettingsQuery.data?.audioRecorder?.autoDeletionEnabled;
    const isDeleteRecordingEnabled =
      userSettingsQuery.data?.audioRecorder?.autoDeleteOnNoteSigning ?? false;
    if (deleteFreq) {
      setAutoDeleteAfterDays(deleteFreq);
    }
    setAutoDeleteTranscript(isDeleteEnabled ?? false);
    setAutoDeleteRecordingsOnNoteSignings(isDeleteRecordingEnabled);
  }, [
    userSettingsQuery.data?.audioRecorder?.autoDeletionFrequency,
    userSettingsQuery.data?.audioRecorder?.autoDeletionEnabled,
    userSettingsQuery.data?.audioRecorder?.autoDeleteOnNoteSigning,
  ]);

  async function toggleAutoDelete() {
    try {
      const newSettings = {
        ...userSettingsQuery.data,
        audioRecorder: {
          autoDeletionEnabled: autoDeleteTranscript,
          autoDeletionFrequency: autoDeleteTranscript
            ? autoDeleteAfterDays
            : undefined,
          autoDeleteOnNoteSigning: autoDeleteRecordingsOnNoteSignings,
        },
      } as UserSettings;
      updateUserSettingsMutation.mutate(newSettings);
    } catch (error) {
      setError(error);
    }
  }

  useEffect(() => {
    if (!orchidPro || !orchidPro.ai_settings) return;
    setSuggestCPT(orchidPro.ai_settings.suggest_cpt_codes);
    setSuggestICD(orchidPro.ai_settings.suggest_diagnosis);
    setDraftTreatmentPlan(orchidPro.ai_settings.draft_treatment_plan);
    setServiceProvider(orchidPro.ai_settings.service_provider_identifier);
    setServiceReceiver(orchidPro.ai_settings.service_receiver_identifier);
    setCustomizeNoteStyle(orchidPro.ai_settings.customize_note_style);
    setNoteFormatting(orchidPro.ai_settings.note_formatting);
    setNoteTone(orchidPro.ai_settings.note_tone);
    setNoteVerbosity(orchidPro.ai_settings.note_verbosity);
    setEnableAILearning(orchidPro.ai_settings.enable_ai_learning);
    setTags(orchidPro.ai_settings.terminology_tags);
    setServiceProviderOptions(getServiceProviderOptions(orchidPro));
    setEnableAIVideo(orchidPro.ai_settings.default_enable_ai_video || false);
    setEnableRecorder(
      orchidPro.ai_settings.default_enable_audio_recorder || false,
    );
    setAutoDeleteTranscript(
      orchidPro.ai_settings.auto_delete_transcript || false,
    );
    setEnableSmartGoals(orchidPro.ai_settings.enable_smart_goals || false);
    setLanguage(orchidPro.ai_settings.default_language || "English");
  }, [orchidPro]);

  const onNoteFormattingChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setNoteFormatting(inputField.value);
  };

  const onNoteToneChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setNoteTone(inputField.value);
  };

  const onNoteVerbosityChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setNoteVerbosity(inputField.value);
  };

  const currNoteFormatting = NOTE_FORMATTING.find(
    (el) => el.value === noteFormatting,
  );

  const currNoteTone = NOTE_TONE.find((el) => el.value === noteTone);

  const currNoteVerbosity = NOTE_VERBOSITY.find(
    (el) => el.value === noteVerbosity,
  );

  const saveAISettings = async () => {
    const AISettingsEntry = {
      suggest_cpt_codes: suggestCPT,
      suggest_diagnosis: suggestICD,
      draft_treatment_plan: draftTreatmentPlan,
      service_provider_identifier: serviceProvider,
      service_receiver_identifier: serviceReceiver,
      customize_note_style: customizeNoteStyle,
      model_note: !patientNote ? [] : [patientNote.note_data_id],
      note_formatting: noteFormatting,
      note_tone: noteTone,
      note_verbosity: noteVerbosity,
      enable_ai_learning: enableAILearning,
      terminology_tags: tags,
      default_enable_ai_video: enableAIVideo,
      default_enable_audio_recorder: enableRecorder,
      auto_delete_transcript: autoDeleteTranscript,
      enable_smart_goals: enableSmartGoals,
      default_language: language,
    };
    await toggleAutoDelete(); // auto-delete-transcriptions

    try {
      setOrchidPro(
        await postOrchidPro({
          ...orchidPro,
          ai_settings: AISettingsEntry,
        }),
      );
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
    }
  };

  const [showCustomNameProviderModal, setShowCustomNameProviderModal] =
    useState(false);
  const [showCustomNameReceiverModal, setShowCustomNameReceiverModal] =
    useState(false);

  const [customNameProvider, setCustomNameProvider] = useState("");
  const [customNameReceiver, setCustomNameReceiver] = useState("");

  const saveCustomNameProviderToDB = () => {};

  const saveCustomNameReceiverToDB = () => {};

  const pickModelNote = useCallback((el) => {
    setChanged(true);
    setCustomizeNoteStyle(false);
    setPatientNote(el);
  }, []);

  return (
    <div className={styles["ai-setting"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <ConfirmModal
        labelTitle={"Add Custom Name"}
        children={
          <>
            <div className={styles["ai-setting__modal-text"]}>
              Enter a custom name to use.
            </div>
            <Form.Control
              type="text"
              placeholder="Enter custom name"
              value={customNameProvider}
              onChange={(e) => {
                setCustomNameProvider(e.target.value);
              }}
            />
          </>
        }
        labelConfirm={"Add"}
        labelCancel={"Cancel"}
        show={showCustomNameProviderModal}
        onCancel={() => setShowCustomNameProviderModal(false)}
        onConfirm={() => {
          setServiceProviderOptions((prev) => [
            ...prev,
            { label: customNameProvider, value: customNameProvider },
          ]);
          setServiceProvider(customNameProvider);
          saveCustomNameProviderToDB();
          setShowCustomNameProviderModal(false);
        }}
      />

      <ConfirmModal
        labelTitle={"Add Custom Name"}
        children={
          <>
            <div className={styles["ai-setting__modal-text"]}>
              Enter a custom name to use.
            </div>
            <Form.Control
              type="text"
              placeholder="Enter custom name"
              value={customNameReceiver}
              onChange={(e) => {
                setCustomNameReceiver(e.target.value);
              }}
            />
          </>
        }
        labelConfirm={"Add"}
        labelCancel={"Cancel"}
        show={showCustomNameReceiverModal}
        onCancel={() => setShowCustomNameReceiverModal(false)}
        onConfirm={() => {
          setServiceReceiverOptions((prev) => [
            ...prev,
            { label: customNameReceiver, value: customNameReceiver },
          ]);
          setServiceReceiver(customNameReceiver);
          saveCustomNameReceiverToDB();
          setShowCustomNameReceiverModal(false);
        }}
      />
      <div className={styles["ai-setting__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>AI Settings</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/ai-setting?tab=${k}`, { replace: true });
          }}
          className={styles["ai-setting__tabs"]}
        >
          {AI_SETTING_OPTIONS.map((el, i) => {
            return (
              <Tab
                key={i}
                eventKey={el.value}
                title={el.label}
                tabClassName={styles["ai-setting__tab"]}
                onEnter={() =>
                  track(AMPLITUDE_PAGE_NAME, "ai-setting-tab", "click", {
                    eventProperties: { tab: el.value },
                  })
                }
              ></Tab>
            );
          })}
        </Tabs>
      </div>

      <div className={styles["ai-setting__body"]}>
        {tab === "ai-notes" ? (
          <>
            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-2",
                  styles["ai-setting__section-title"],
                )}
              >
                Clinical Assistance Settings
              </div>
              <div className={"rg-14 mb-4"}>
                Optimize your clinical workflow by enabling the following
                AI-driven support features.
              </div>
              <div className={styles["ai-setting__toggle"]}>
                <ToggleSwitch
                  checked={suggestCPT}
                  onChange={(e) => {
                    setChanged(true);
                    setSuggestCPT(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "suggest-cpt-toggle", "click")
                  }
                />
                <span aria-hidden="true">{"Suggest CPT Codes"}</span>
              </div>
              <div className={styles["ai-setting__toggle"]}>
                <ToggleSwitch
                  checked={suggestICD}
                  onChange={(e) => {
                    setChanged(true);
                    setSuggestICD(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "suggest-icd-toggle", "click")
                  }
                />
                <span aria-hidden="true">{"Suggest Diagnosis"}</span>
              </div>
              <div className={styles["ai-setting__toggle"]}>
                <ToggleSwitch
                  checked={draftTreatmentPlan}
                  onChange={(e) => {
                    setChanged(true);
                    setDraftTreatmentPlan(e.target.checked);
                  }}
                  onClick={() =>
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "draft-treatment-plan-toggle",
                      "click",
                    )
                  }
                />
                <span aria-hidden="true">{"Draft Treatment Plan"}</span>
              </div>
            </div>

            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-2",
                  styles["ai-setting__section-title"],
                )}
              >
                Clinical Note Identifiers
              </div>
              <div className={"rg-14 mb-4"}>
                Set your preferred terms for how we should refer to you and your
                clients in your clinical notes.
              </div>
              <Row className={styles["ai-setting__note-identifiers"]}>
                <Form.Group as={Col} md="6" controlId="service_provider">
                  <Form.Label className="rg-12 gray01">
                    How Orchid will refer to you, the service provider
                  </Form.Label>
                  <DropdownToggle
                    title={serviceProvider}
                    Caret={<OutlineChevronDown />}
                  >
                    {serviceProviderOptions.map((el) => (
                      <Dropdown.Item
                        key={el.value}
                        onClick={() => {
                          setChanged(true);
                          setServiceProvider(el.value);
                        }}
                      >
                        {el.label}
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Item
                      disabled
                      className={
                        styles["ai-setting__custom-name-dropdown-item"]
                      }
                      onClick={() => setShowCustomNameProviderModal(true)}
                    >
                      <OutlinePlus16 /> Add Custom Name
                    </Dropdown.Item>
                  </DropdownToggle>
                </Form.Group>
                <Form.Group as={Col} md="6" controlId="service_receiver">
                  <Form.Label className="rg-12 gray01">
                    How Orchid will refer to the service receiver
                  </Form.Label>
                  <DropdownToggle
                    title={serviceReceiver}
                    Caret={<OutlineChevronDown />}
                  >
                    {serviceReceiverOptions.map((el) => (
                      <Dropdown.Item
                        key={el.value}
                        onClick={() => {
                          setChanged(true);
                          setServiceReceiver(el.value);
                        }}
                      >
                        {el.label}
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Item
                      className={
                        styles["ai-setting__custom-name-dropdown-item"]
                      }
                      disabled
                      onClick={() => setShowCustomNameReceiverModal(true)}
                    >
                      <OutlinePlus16 /> Add Custom Name
                    </Dropdown.Item>
                  </DropdownToggle>
                </Form.Group>
              </Row>
            </div>

            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-2",
                  styles["ai-setting__section-title"],
                )}
              >
                Clinical Note Formatting & Style
              </div>
              <div className={"rg-14 mb-4"}>
                Choose to manually fine-tune your clinical note's formatting &
                style, <br />
                or train Orchid’s AI using your preferred clinical note to
                automatically adapt to your documentation preferences.
              </div>

              <div className={styles["ai-setting__note-styles-selection"]}>
                {[
                  {
                    value: "custom-note-style",
                    label: "Customize Formatting & Style",
                  },
                  {
                    value: "train-ai-model",
                    label: "Train AI with Model Note",
                  },
                ].map((option, i) => {
                  return (
                    <RadioButton
                      key={i}
                      className={styles["ai-setting__radio"]}
                      id={option.value}
                      label={option.label}
                      value={option.value}
                      name="note-styles-option"
                      onChange={(e) => {
                        setChanged(true);
                        setCustomizeNoteStyle(
                          e.target.value === "custom-note-style",
                        );
                      }}
                      checked={
                        customizeNoteStyle
                          ? option.value === "custom-note-style"
                          : option.value === "train-ai-model"
                      }
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "note-styles-radio",
                          "click",
                        );
                      }}
                    />
                  );
                })}
              </div>
              {!customizeNoteStyle ? (
                <PreviousNotesList
                  patientNote={patientNote}
                  pickModelNote={pickModelNote}
                  selectedModelNote={orchidPro?.ai_settings?.model_note}
                />
              ) : (
                <>
                  <div className={styles["ai-setting__sub-section"]}>
                    <div className={styles["ai-setting__sub-section-left"]}>
                      <div className={"bd-16 mb-4"}>Formatting</div>
                      {NOTE_FORMATTING.map((option, i) => {
                        return (
                          <RadioButton
                            key={i}
                            className={styles["ai-setting__radio"]}
                            id={option.value}
                            label={option.label}
                            value={option.value}
                            name="note-formatting-option"
                            onChange={onNoteFormattingChange}
                            checked={noteFormatting === option.value}
                            onClick={() => {
                              track(
                                AMPLITUDE_PAGE_NAME,
                                "note-formatting-radio",
                                "click",
                              );
                            }}
                          />
                        );
                      })}
                    </div>
                    {currNoteFormatting && (
                      <div className={styles["ai-setting__sub-section-right"]}>
                        <div
                          className={styles["ai-setting__sub-section-title"]}
                        >
                          {currNoteFormatting.label}
                        </div>
                        <div
                          className={styles["ai-setting__sub-section-text"]}
                          dangerouslySetInnerHTML={{
                            __html: currNoteFormatting.example,
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles["ai-setting__sub-section"]}>
                    <div className={styles["ai-setting__sub-section-left"]}>
                      <div className={"bd-16 mb-4"}>Tone</div>
                      {NOTE_TONE.map((option, i) => {
                        return (
                          <RadioButton
                            key={i}
                            className={styles["ai-setting__radio"]}
                            id={option.value}
                            label={option.label}
                            value={option.value}
                            name="note-tone-option"
                            onChange={onNoteToneChange}
                            checked={noteTone === option.value}
                            onClick={() => {
                              track(
                                AMPLITUDE_PAGE_NAME,
                                "note-tone-radio",
                                "click",
                              );
                            }}
                          />
                        );
                      })}
                    </div>
                    {currNoteTone && (
                      <div className={styles["ai-setting__sub-section-right"]}>
                        <div
                          className={styles["ai-setting__sub-section-title"]}
                        >
                          {currNoteTone.label}
                        </div>
                        <div
                          className={styles["ai-setting__sub-section-text"]}
                          dangerouslySetInnerHTML={{
                            __html: currNoteTone.example,
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles["ai-setting__sub-section"]}>
                    <div className={styles["ai-setting__sub-section-left"]}>
                      <div className={"bd-16 mb-4"}>Verbosity</div>
                      {NOTE_VERBOSITY.map((option, i) => {
                        return (
                          <RadioButton
                            key={i}
                            className={styles["ai-setting__radio"]}
                            id={option.value}
                            label={
                              <>
                                <b>{option.label}</b>
                                <div>{option.note}</div>
                              </>
                            }
                            value={option.value}
                            name="note-verbosity-option"
                            onChange={onNoteVerbosityChange}
                            checked={noteVerbosity === option.value}
                            onClick={() => {
                              track(
                                AMPLITUDE_PAGE_NAME,
                                "note-verbosity-radio",
                                "click",
                              );
                            }}
                          />
                        );
                      })}
                    </div>
                    {currNoteVerbosity && (
                      <div className={styles["ai-setting__sub-section-right"]}>
                        <div
                          className={styles["ai-setting__sub-section-title"]}
                        >
                          {currNoteVerbosity.label}
                        </div>
                        <div
                          className={styles["ai-setting__sub-section-text"]}
                          dangerouslySetInnerHTML={{
                            __html: currNoteVerbosity.example,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-2",
                  styles["ai-setting__section-title"],
                )}
              >
                Train AI on Past Clinical Notes
              </div>
              <div className={"rg-14 mb-4"}>
                Activate this feature to let Orchid AI learn from up to four
                selected past clinical notes, enhancing the composition of
                future documentation. When enabled, you will choose previous
                notes to inform the next session's content.
              </div>

              <div className={styles["ai-setting__toggle"]}>
                <ToggleSwitch
                  checked={enableAILearning}
                  onChange={(e) => {
                    setChanged(true);
                    setEnableAILearning(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "ai-learning-toggle", "click")
                  }
                />
                <span aria-hidden="true">{"Enable AI Learning"}</span>
              </div>
            </div>

            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-2",
                  styles["ai-setting__section-title"],
                )}
              >
                Terminology Preference
              </div>
              <div className={"rg-14 mb-4"}>
                Enter preferred terms or phrases to be included in your notes
                when necessary.
              </div>
              <Form.Group
                controlId="tags"
                className={styles["ai-setting__terminology"]}
              >
                <Form.Label className="rg-12 gray01">Tags</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter tags"
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") {
                      return;
                    }

                    event.preventDefault();

                    const input = event.target as HTMLInputElement;

                    if (!input) {
                      return;
                    }
                    setChanged(true);

                    const tagToAdd = input.value.trim();
                    setTags((prev) => [...prev, tagToAdd]);
                    input.value = "";
                  }}
                />
              </Form.Group>
              <div className={styles["ai-setting__tags"]}>
                {tags.map((tag, i) => (
                  <div key={i} className={styles["ai-setting__tag"]}>
                    {tag}
                    <IconButton
                      onClick={() => {
                        setChanged(true);
                        setTags((prev) =>
                          prev.filter((_, index) => index !== i),
                        );
                      }}
                    >
                      <OutlineCross />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : tab === "recording-preferences" ? (
          <>
            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-3",
                  styles["ai-setting__section-title"],
                )}
              >
                AI Telehealth
              </div>
              <div className={getClasses("mb-0", styles["ai-setting__toggle"])}>
                <ToggleSwitch
                  checked={enableAIVideo}
                  onChange={(e) => {
                    setChanged(true);
                    setEnableAIVideo(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "enable-ai-video", "click")
                  }
                />
                <span aria-hidden="true">
                  Enable AI Telehealth by default for Telehealth sessions.
                </span>
              </div>
            </div>
            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-3",
                  styles["ai-setting__section-title"],
                )}
              >
                AI Audio Recorder
              </div>
              <div className={getClasses("mb-0", styles["ai-setting__toggle"])}>
                <ToggleSwitch
                  checked={enableRecorder}
                  onChange={(e) => {
                    setChanged(true);
                    setEnableRecorder(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "enable-audio-recorder", "click")
                  }
                />
                <span aria-hidden="true">
                  Enable AI Audio Recorder by default for In-person sessions.
                </span>
              </div>
            </div>

            <AutoDeleteTranscript
              autoDeleteAfterDays={autoDeleteAfterDays}
              autoDeleteTranscript={autoDeleteTranscript}
              setChanged={setChanged}
              setAutoDeleteTranscript={setAutoDeleteTranscript}
              setAutoDeleteAfterDays={setAutoDeleteAfterDays}
            />
            <AutoDeleteRecordingsOnNoteSigning
              setChanged={setChanged}
              autoDeleteRecordingsOnNoteSignings={
                autoDeleteRecordingsOnNoteSignings
              }
              setAutoDeleteRecordingsOnNoteSignings={
                setAutoDeleteRecordingsOnNoteSignings
              }
            />

            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-3",
                  styles["ai-setting__section-title"],
                )}
              >
                Default Language
              </div>

              <div className={styles["ai-setting__setting-text"]}>
                Set your default language for transcription during all types of
                recording (Orchid Video recording, Audio recording, dictation,
                and more).
              </div>

              <Form.Group className={styles["ai-setting__language"]}>
                <CustomSelect
                  options={LANGUAGE_SPOKEN_OPTIONS}
                  onChange={(el) => {
                    setChanged(true);
                    setLanguage(el.value);
                  }}
                  required
                  value={LANGUAGE_SPOKEN_OPTIONS.find(
                    (option) => option.value === language,
                  )}
                />
              </Form.Group>
            </div>
          </>
        ) : tab === "treatment-plan" ? (
          <>
            <div className={styles["ai-setting__section"]}>
              <div
                className={getClasses(
                  "mb-3",
                  styles["ai-setting__section-title"],
                )}
              >
                SMART Goals Integration{" "}
                <span className={styles["ai-setting__beta-tag"]}>Beta</span>
              </div>
              <div className={"rg-14 mb-4"}>
                Enable this option to incorporate the SMART Goal framework into
                your Treatment Plan. SMART goals are Specific, Measurable,
                Achievable, Relevant, and Time-bound, helping you create clear
                and actionable objectives for your clients. Toggle off to
                disable SMART goal integration.
              </div>
              <div className={getClasses("mb-0", styles["ai-setting__toggle"])}>
                <ToggleSwitch
                  checked={enableSmartGoals}
                  onChange={(e) => {
                    setChanged(true);
                    setEnableSmartGoals(e.target.checked);
                  }}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "enable-smart-goals", "click")
                  }
                />

                <span aria-hidden="true">Use SMART Goal Framework</span>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <div className={styles["ai-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2024 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveAISettings();
          }}
          className={styles["data-analysis__save-button"]}
          disabled={!changed}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AISetting;
